import { db } from "../firebase/firebase";
import { collection, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { UserProfile, UserProgress } from "../types/types";

const userCollection = collection(db, "users");

export const userService = {
  getUserById: async (id: string): Promise<UserProfile | null> => {
    const userDoc = await getDoc(doc(userCollection, id));
    return userDoc.exists()
      ? ({ id: userDoc.id, ...userDoc.data() } as UserProfile)
      : null;
  },

  addUser: async (user: UserProfile): Promise<string> => {
    const userRef = doc(userCollection, user.id);
    user.progress = {
      courses: [],
      dayStreaks: [],
      total_score: 0,
    };
    await setDoc(userRef, user);
    return user.id;
  },

  getUserProgress: async (userId: string) => {
    const userDoc = await getDoc(doc(userCollection, userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData?.progress || {};
    }
    throw new Error("User not found");
  },

  updateLessonProgress: async (
    userId: string,
    courseId: string,
    lessonId: string,
    completedAt: string,
    score: number
  ) => {
    const userRef = doc(userCollection, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      await setDoc(userRef, {
        progress: { courses: [], coursesStarted: [], dayStreaks: [], total_score:0 },
      });
      return;
    }

    const userData = userDoc.data();
    const userProgress: UserProgress = userData?.progress ?? {};
    let courseEntry = userProgress.courses.find((c) => c.courseId === courseId);
    const now = new Date().toISOString();
    if (!courseEntry) {
      courseEntry = {
        courseId,
        startedAt: completedAt,
        lessonProgress: [{ lessonId, completedAt }],
        lastAccessedAt: now, // Set lastAccessedAt when course is first created
      };
      userProgress.courses.push(courseEntry);
    } else {
      const lessonIndex = courseEntry.lessonProgress.findIndex(
        (lesson) => lesson.lessonId === lessonId
      );
      if (lessonIndex === -1) {
        courseEntry.lessonProgress.push({ lessonId, completedAt });
      }
      courseEntry.lastAccessedAt = now; // Update lastAccessedAt on lesson completion
    }

    const today = new Date().toDateString();
    const dayStreakEntry = userProgress.dayStreaks.findIndex(
      (d) => d === today
    );
    if (dayStreakEntry === -1) {
      userProgress.dayStreaks.push(today);
    }
    // Update total score
    const currentScore = userData?.progress?.total_score ?? 0;
    const updatedScore = currentScore + score;

    await updateDoc(userRef, {
      progress: userProgress,
      "progress.total_score": updatedScore,
    });
  },
};
